export const BI_FULL_ACCESS_GROUP = "Gp-Forida-BI-FullAccess";
export const BI_SALES_GROUP = "Gp-Forida-Sales";
export const BI_PROJECT_MGR_GROUP = "Gp-Forida-BI-ProjectMgr";
export const BI_PROJ_USER_GROUP = "GP-Forida-BI-ProjUser";

export const HELPDESK_DASHBOARD_PATH = "/odoo/helpdesk-dashboard";
export const ODOO_CUSTOMER_PATH = "/odoo/customer";
export const ODOO_CUSTOMER_MULTI_LICENSES_PATH = "/odoo/customer/multi-licenses-overview";
export const SALES_RANKING_PATH = "/odoo/sales-ranking-insights";
export const SALES_INTELLIGENCE_PATH = "/autodesk-usage/sales-intelligence";
export const AUTODESK_CUSTOMER_PATH = "/autodesk-usage/corporate";
export const OPPORTUNITY_DASHBOARD_PATH = "/opportunity-dashboard";
const BI_RESTRICTED_PATHS = new Set([
  HELPDESK_DASHBOARD_PATH,
  ODOO_CUSTOMER_PATH,
  ODOO_CUSTOMER_MULTI_LICENSES_PATH,
  SALES_RANKING_PATH,
  SALES_INTELLIGENCE_PATH,
  AUTODESK_CUSTOMER_PATH,
  OPPORTUNITY_DASHBOARD_PATH,
]);

export function normalizeGroupName(groupName: string | null | undefined): string {
  return String(groupName ?? "").trim().toLowerCase();
}

export function normalizeGroupCn(groupName: string | null | undefined): string {
  const value = String(groupName ?? "").trim();
  if (!value) return "";
  const firstPart = value.split(",", 1)[0].trim();
  if (firstPart.toLowerCase().startsWith("cn=")) {
    return firstPart.slice(3).trim().toLowerCase();
  }
  return value.toLowerCase();
}

export function hasBiFullAccess(groups?: string[] | null): boolean {
  return (groups ?? []).some(
    (group) => normalizeGroupCn(group) === normalizeGroupName(BI_FULL_ACCESS_GROUP)
  );
}

export function hasBiSalesAccess(groups?: string[] | null): boolean {
  return (groups ?? []).some(
    (group) => normalizeGroupCn(group) === normalizeGroupName(BI_SALES_GROUP)
  );
}

export function hasBiProjectMgrAccess(groups?: string[] | null): boolean {
  return (groups ?? []).some(
    (group) => normalizeGroupCn(group) === normalizeGroupName(BI_PROJECT_MGR_GROUP)
  );
}

export function hasBiProjUserAccess(groups?: string[] | null): boolean {
  return (groups ?? []).some((group) => {
    const normalized = normalizeGroupCn(group);
    return (
      normalized === normalizeGroupName(BI_PROJECT_MGR_GROUP) ||
      normalized === normalizeGroupName(BI_PROJ_USER_GROUP)
    );
  });
}

export function canAccessHelpdeskDashboard(groups?: string[] | null): boolean {
  return hasBiProjectMgrAccess(groups) || hasBiProjUserAccess(groups) || hasBiFullAccess(groups);
}

export function canAccessSalesRankingInsights(groups?: string[] | null): boolean {
  return hasBiFullAccess(groups);
}

export function canAccessSalesIntelligence(groups?: string[] | null): boolean {
  return hasBiFullAccess(groups);
}

export function canAccessOpportunityDashboard(groups?: string[] | null): boolean {
  return hasBiFullAccess(groups);
}

export function canAccessAutodeskCustomer(groups?: string[] | null): boolean {
  return hasBiSalesAccess(groups) || hasBiFullAccess(groups);
}

export function canAccessOdooCustomers(groups?: string[] | null): boolean {
  return hasBiSalesAccess(groups) || hasBiFullAccess(groups);
}

export function getDefaultLandingPath(groups?: string[] | null): string {
  if (hasBiFullAccess(groups)) return SALES_RANKING_PATH;
  if (hasBiProjUserAccess(groups)) return HELPDESK_DASHBOARD_PATH;
  if (hasBiSalesAccess(groups)) return AUTODESK_CUSTOMER_PATH;
  return AUTODESK_CUSTOMER_PATH;
}

function canAccessPath(path: string, groups?: string[] | null): boolean {
  if (path === HELPDESK_DASHBOARD_PATH) return canAccessHelpdeskDashboard(groups);
  if (path === ODOO_CUSTOMER_PATH || path.startsWith(`${ODOO_CUSTOMER_PATH}/`)) {
    return canAccessOdooCustomers(groups);
  }
  if (path === SALES_RANKING_PATH) return canAccessSalesRankingInsights(groups);
  if (path === SALES_INTELLIGENCE_PATH) return canAccessSalesIntelligence(groups);
  if (path === AUTODESK_CUSTOMER_PATH) return canAccessAutodeskCustomer(groups);
  if (path === OPPORTUNITY_DASHBOARD_PATH) return canAccessOpportunityDashboard(groups);
  return true;
}

export function resolvePostLoginRedirect(
  redirect: string | null | undefined,
  groups?: string[] | null
): string {
  const defaultLanding = getDefaultLandingPath(groups);
  if (!redirect) return defaultLanding;
  if (BI_RESTRICTED_PATHS.has(redirect) && !canAccessPath(redirect, groups)) {
    return defaultLanding;
  }
  return redirect;
}
